import * as React from 'react';

let scrollLockCount = 0;
let savedOverflow = '';
let savedGutter = '';

function lockDocumentScroll() {
    const html = document.documentElement;
    if (scrollLockCount === 0) {
        savedOverflow = html.style.overflow;
        savedGutter = html.style.getPropertyValue('scrollbar-gutter');
        // Keep the layout from shifting when a classic scrollbar disappears.
        if (window.innerWidth > html.clientWidth) {
            html.style.setProperty('scrollbar-gutter', 'stable');
        }
        html.style.overflow = 'hidden';
    }
    scrollLockCount += 1;
}

function unlockDocumentScroll() {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount > 0) return;
    const html = document.documentElement;
    html.style.overflow = savedOverflow;
    if (savedGutter) {
        html.style.setProperty('scrollbar-gutter', savedGutter);
    } else {
        html.style.removeProperty('scrollbar-gutter');
    }
}

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusable(container: HTMLElement): HTMLElement[] {
    const candidates = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    // Skip hidden controls when layout is available (environments without layout report no rects).
    if (container.getClientRects().length === 0) {
        return candidates;
    }
    return candidates.filter((element) => element.getClientRects().length > 0);
}

function getCurrentElement(ref?: React.RefObject<HTMLElement>): HTMLElement | null {
    return ref?.current ?? null;
}

/**
 * Modal sheet behavior: exposes the panel as a labelled dialog, moves focus into it, keeps Tab
 * inside, closes on Escape (unless a popup inside is open), and returns focus to the opener.
 */
export function useModalDialog({
    open,
    onClose,
    role = 'dialog',
    initialFocusRef,
    returnFocusRef,
}: {
    open: boolean;
    onClose: () => void;
    role?: 'dialog' | 'alertdialog';
    initialFocusRef?: React.RefObject<HTMLElement>;
    returnFocusRef?: React.RefObject<HTMLElement>;
}) {
    const panelRef = React.useRef<HTMLDivElement>(null);
    const titleId = React.useId();
    const descriptionId = React.useId();
    const onCloseRef = React.useRef(onClose);
    onCloseRef.current = onClose;

    React.useEffect(() => {
        if (!open) {
            return;
        }
        lockDocumentScroll();
        const opener =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const panel = panelRef.current;
        if (panel && !panel.contains(document.activeElement)) {
            (initialFocusRef?.current ?? panel).focus({ preventScroll: true });
        }
        let recoverFrame = 0;
        const handleFocusOut = (event: FocusEvent) => {
            if (event.relatedTarget) return;
            cancelAnimationFrame(recoverFrame);
            recoverFrame = requestAnimationFrame(() => {
                const active = document.activeElement;
                if (panel?.isConnected && (!active || active === document.body)) {
                    panel.focus({ preventScroll: true });
                }
            });
        };
        panel?.addEventListener('focusout', handleFocusOut);
        return () => {
            panel?.removeEventListener('focusout', handleFocusOut);
            cancelAnimationFrame(recoverFrame);
            unlockDocumentScroll();
            const candidates = [
                opener,
                getCurrentElement(returnFocusRef),
                document.getElementById('main-content'),
            ];
            for (const el of candidates) {
                if (
                    !el ||
                    el === document.body ||
                    !el.isConnected ||
                    (el as HTMLButtonElement).disabled
                ) {
                    continue;
                }
                el.focus({ preventScroll: true });
                if (document.activeElement === el) break;
            }
        };
    }, [initialFocusRef, open, returnFocusRef]);

    const onKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        const panel = panelRef.current;
        if (!panel) {
            return;
        }
        if (event.key === 'Escape') {
            if (event.defaultPrevented || panel.querySelector('[aria-expanded="true"]')) {
                return;
            }
            event.stopPropagation();
            onCloseRef.current();
            return;
        }
        if (event.key !== 'Tab') {
            return;
        }
        const focusable = getFocusable(panel);
        if (focusable.length === 0) {
            event.preventDefault();
            return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (event.shiftKey && (active === first || active === panel)) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && (active === last || active === panel)) {
            event.preventDefault();
            first.focus();
        }
    }, []);

    return {
        titleId,
        descriptionId,
        panelProps: {
            ref: panelRef,
            role,
            'aria-modal': true,
            'aria-labelledby': titleId,
            tabIndex: -1,
            onKeyDown,
        },
    };
}
